/*

  Gamelist.ts

  List of games in progress display component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, GameListItem } from "./types";
import { ts, mt } from "./i18n";
import { Component, ComponentFunc, VnodeChildren, m } from "./mithril";
import { gameUrl, glyph, glyphGrayed, nbsp } from "./util";
import { ProModeIndicator, UserInfoButton } from "./components";

interface IHintAttributes {
  loadingGameList: boolean;
  loadingAllLists: boolean;
  gameList: GameListItem[] | null;
}

const Hint: Component<IHintAttributes> = {
  view: (vnode) => {
    const { loadingGameList, loadingAllLists, gameList } = vnode.attrs;
    // Show some help if the user has no games in progress
    if (loadingGameList || loadingAllLists || (gameList !== null && gameList.length > 0))
      // Either we have games in progress or the game list is being loaded
      return "";
    return m(".hint", { style: { display: "block" } },
      [
        m("p",
          [
            "Ef þig vantar einhvern til að skrafla við, veldu flipann ",
            m(m.route.Link,
              { href: "/main?tab=2" },
              [glyph("user"), nbsp(), "Andstæðingar"]
            ),
            " og skoraðu á tölvuþjarka - ",
            glyph("cog"), nbsp(), m("b", "Amlóða"),
            ", ",
            glyph("cog"), nbsp(), m("b", "Miðlung"),
            " eða ",
            glyph("cog"), nbsp(), m("b", "Fullsterkan"),
            " - eða veldu þér annan leikmann úr stafrófs\u00ADlistunum " + // Soft hyphen
            " sem þar er að finna til að skora á."
          ]
        ),
        m("p",
          [
            "Þú stofnar áskorun með því að smella á bendi-teiknið ",
            glyph("hand-right", { style: { "margin-left": "6px", "margin-right": "6px" } }),
            " vinstra megin við nafn andstæðingsins."
          ]
        ),
        m("p",
          "Tölvuþjarkarnir eru ætíð reiðubúnir að skrafla og viðureign við þá " +
          " hefst strax. Aðrir leikmenn þurfa að samþykkja áskorun áður en viðureign hefst."
        ),
        m("p.no-mobile-block",
          [
            m(m.route.Link, { href: "/help" }, "Hjálp"),
            " má fá með því að smella á bláa ",
            glyph("info-sign"), nbsp(), "-", nbsp(),
            "teiknið hér til vinstri."
          ]
        ),
        m("p.no-mobile-block",
          "Þú kemst alltaf aftur í þessa aðalsíðu með því að smella á " +
          "örvarmerkið efst vinstra megin við skraflborðið."
        )
      ]
    );
  }
};

interface IGameListItemAttrs {
  view: IView;
  item: GameListItem;
  i: number;
}

const GameListEntry: ComponentFunc<IGameListItemAttrs> = () => {
    // Generate a list item about a game in progress (or recently finished)

    function vwOpp(item: GameListItem): VnodeChildren {
      const arg = item.oppid === null ? [glyph("cog"), nbsp(), item.opp] : item.opp;
      return m("span.list-opp", { title: item.fullname }, arg);
    }

    function vwTurn(item: GameListItem): VnodeChildren {
      let turnText = "";
      let flagClass = "";
      if (item.my_turn) {
        turnText = ts("Þú átt leik");
      }
      else if (item.zombie) {
        turnText = ts("Viðureign lokið");
        flagClass = ".zombie";
      }
      else {
        // {opponent}'s move
        turnText = ts("opp_move", { opponent: item.opp });
        flagClass = ".grayed";
      }
      return m("span.list-myturn",
        m("span.glyphicon.glyphicon-flag" + flagClass, { title: turnText })
      );
    }

    function vwOverdue(item: GameListItem): VnodeChildren {
      if (item.overdue)
        return glyph("hourglass",
          { title: item.my_turn ? "Er að renna út á tíma" : "Getur þvingað fram uppgjöf" }
        );
      return glyphGrayed("hourglass");
    }

    function vwTileCount(item: GameListItem): VnodeChildren {
      const winLose = item.sc0 < item.sc1 ? ".losing" : "";
      return m(".tilecount",
        m(".tc" + winLose, { style: { width: item.tile_count.toString() + "%" } })
      );
    }

    return {
      view: (vnode) => {
        const { view, item, i } = vnode.attrs;
        return m(".listitem" + (i % 2 === 0 ? ".oddlist" : ".evenlist"),
          [
            m(m.route.Link,
              { href: gameUrl(item.url) },
              [
                vwTurn(item),
                m("span.list-overdue", vwOverdue(item)),
                m("span.list-ts-short", item.ts),
                vwOpp(item)
              ]
            ),
            m(UserInfoButton, {
              view,
              item: { userid: item.oppid, nick: item.opp, fullname: item.fullname },
            }),
            m("span.list-s0", item.sc0),
            m("span.list-colon", ":"),
            m("span.list-s1", item.sc1),
            m("span.list-tc", vwTileCount(item)),
            m(ProModeIndicator, { manual: item.prefs?.manual })
          ]
      );
    }
  }
};

interface IAttributes {
  view: IView;
  id: string;
}

export const GameList: ComponentFunc<IAttributes> = () => {
  // Shows a list of games in progress, obtained from view.model.gameList

  return {
    view: (vnode) => {
      const { view, id } = vnode.attrs;
      const { model } = view;
      const { gameList, loadingGameList, loadingAllLists } = model;
      // Trigger loading of game list if needed
      if (gameList === null)
        model.loadGameList();
      return [
        m(".listitem.listheader",
          [
            m("span.list-myturn", glyphGrayed("flag", { title: ts('Átt þú leik?') })),
            m("span.list-overdue",
              glyphGrayed("hourglass", { title: ts('Langt frá síðasta leik?') })
            ),
            mt("span.list-ts-short", "Síðasti leikur"),
            mt("span.list-opp", "Andstæðingur"),
            mt("span.list-info-hdr", "Ferill"),
            mt("span.list-scorehdr", "Staða"),
            mt("span.list-tc", "Framvinda"),
            m(ProModeIndicator, { header: true }),
          ]
        ),
        m("div",
          { id },
          !gameList ? "" : gameList.map((item, i) => m(GameListEntry, { view, item, i }))
        ),
        m(Hint, { loadingGameList, loadingAllLists, gameList }),
      ];
    }
  };
};
